// src/business/components/erp/DataTable.jsx
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
  return sortDir === "asc"
    ? <ChevronUp className="h-3.5 w-3.5" />
    : <ChevronDown className="h-3.5 w-3.5" />;
}

/**
 * Reusable data table with sorting, loading, and empty states.
 *
 * @param {Object} props
 * @param {Array<{ key, label, sortable?, render?, className?, headerClass? }>} props.columns
 * @param {Array} props.data
 * @param {boolean} props.loading
 * @param {string} props.emptyMessage
 * @param {string} props.sortField
 * @param {string} props.sortDir
 * @param {Function} props.onSort
 * @param {Function} props.onRowClick
 */
export default function DataTable({
  columns,
  data = [],
  loading = false,
  emptyMessage = "No data found.",
  sortField,
  sortDir,
  onSort,
  onRowClick,
}) {
  const handleSort = (col) => {
    if (!col.sortable || !onSort) return;
    const newDir = sortField === col.key && sortDir === "asc" ? "desc" : "asc";
    onSort(col.key, newDir);
  };

  return (
    <div className="operations-table-wrap">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col)}
                className={[
                  "px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wider",
                  "text-neutral-500 dark:text-neutral-400",
                  col.sortable ? "cursor-pointer select-none hover:text-neutral-700 dark:hover:text-neutral-200" : "",
                  col.headerClass || "",
                ].join(" ")}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />}
                </span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skel-${i}`}>
                {columns.map((col) => (
                  <td key={col.key} className="px-3.5 py-2.5">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-neutral-500 dark:text-neutral-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={row.id || idx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={[
                  "text-neutral-700 dark:text-neutral-200",
                  onRowClick ? "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/40" : "",
                ].join(" ")}
              >
                {columns.map((col) => (
                  <td key={col.key} className={`px-3.5 py-2.5 ${col.className || ""}`}>
                    {col.render ? col.render(row) : row[col.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
